import { Input } from '@tigergraph/app-ui-lib/input';
import { Select } from '@tigergraph/app-ui-lib/select';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Search } from 'baseui/icon';
import { Value } from 'baseui/select';
import { MdOutlineAccountTree, MdGridOn, MdFormatListBulleted } from 'react-icons/md';

import { AccessFilter, FilterState, SizeFilter, SortKey, StatusFilter, ViewMode } from './types';

type FilterOption<T extends string> = { id: T; label: string };

const STATUS_OPTIONS: FilterOption<StatusFilter>[] = [
  { id: 'active', label: 'Active' },
  { id: 'stopped', label: 'Stopped' },
  { id: 'idle', label: 'Idle' },
  { id: 'pending', label: 'Pending' },
  { id: 'degraded', label: 'Degraded' },
  { id: 'error', label: 'Error' },
  { id: 'terminating', label: 'Terminating' },
];

const ACCESS_OPTIONS: FilterOption<AccessFilter>[] = [
  { id: 'rw', label: 'R/W' },
  { id: 'ro', label: 'R/O' },
];

const SORT_OPTIONS: FilterOption<SortKey>[] = [
  { id: 'name_asc', label: 'Name (A → Z)' },
  { id: 'name_desc', label: 'Name (Z → A)' },
  { id: 'size_desc', label: 'Size (High → Low)' },
  { id: 'size_asc', label: 'Size (Low → High)' },
];

export type ToolbarProps = {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  sizeOptions: string[];
};

export default function Toolbar({ viewMode, onViewModeChange, filters, onFiltersChange, sizeOptions }: ToolbarProps) {
  const [css, theme] = useStyletron();

  const sizeOptionsList: FilterOption<SizeFilter>[] = sizeOptions.map((size) => ({ id: size, label: size }));

  const findOption = <T extends string>(options: FilterOption<T>[], id: T): Value =>
    id === ('all' as T) ? [] : options.filter((option) => option.id === id);

  const selectRootStyle = {
    minWidth: '140px',
    maxWidth: '180px',
  };

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        backgroundColor: theme.colors['background.alternative'],
        padding: '20px',
        borderRadius: '12px',
        border: `1px solid ${theme.colors.divider}`,
      })}
    >
      <div
        className={css({
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap',
        })}
      >
        <ViewModeToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
        <Input
          value={filters.search}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              search: e.currentTarget.value,
            })
          }
          placeholder="Search by database or workspace name"
          startEnhancer={<Search />}
          clearable={true}
          overrides={{
            Root: {
              style: {
                flex: 1,
                minWidth: '240px',
                maxWidth: '420px',
              },
            },
          }}
        />
      </div>
      <div
        className={css({
          display: 'flex',
          flex: 1,
          gap: '8px',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          marginLeft: '12px',
        })}
      >
        <Select
          options={STATUS_OPTIONS}
          labelKey="label"
          valueKey="id"
          placeholder="All Status"
          value={findOption(STATUS_OPTIONS, filters.status)}
          onChange={(params) => {
            const id = (params.value[0]?.id as StatusFilter) ?? 'all';
            onFiltersChange({ ...filters, status: id });
          }}
          overrides={{ Root: { style: selectRootStyle } }}
          aria-label="Filter by status"
        />
        <Select
          options={ACCESS_OPTIONS}
          labelKey="label"
          valueKey="id"
          placeholder="All Access"
          value={findOption(ACCESS_OPTIONS, filters.access)}
          onChange={(params) => {
            const id = (params.value[0]?.id as AccessFilter) ?? 'all';
            onFiltersChange({ ...filters, access: id });
          }}
          overrides={{ Root: { style: selectRootStyle } }}
          aria-label="Filter by access"
        />
        <Select
          options={sizeOptionsList}
          labelKey="label"
          valueKey="id"
          placeholder="All Sizes"
          value={findOption(sizeOptionsList, filters.size)}
          onChange={(params) => {
            const id = (params.value[0]?.id as SizeFilter) ?? 'all';
            onFiltersChange({ ...filters, size: id });
          }}
          overrides={{ Root: { style: selectRootStyle } }}
          aria-label="Filter by size"
        />
        <Select
          clearable={false}
          searchable={false}
          options={SORT_OPTIONS}
          labelKey="label"
          valueKey="id"
          value={findOption(SORT_OPTIONS, filters.sort)}
          onChange={(params) => {
            const id = (params.value[0]?.id as SortKey) ?? 'name_asc';
            onFiltersChange({ ...filters, sort: id });
          }}
          overrides={{ Root: { style: { ...selectRootStyle, minWidth: '200px', maxWidth: '220px' } } }}
          aria-label="Sort workspaces"
        />
      </div>
    </div>
  );
}

function ViewModeToggle({
  viewMode,
  onViewModeChange,
}: {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}) {
  const [css, theme] = useStyletron();

  const buttonStyle = (active: boolean) =>
    css({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      cursor: 'pointer',
      backgroundColor: active ? theme.colors['background.selected.subtlest'] : 'transparent',
      color: active ? theme.colors['button.background.primary'] : theme.colors['text.secondary'],
      border: 'none',
      borderRadius: '4px',
      ':hover': {
        backgroundColor: active ? theme.colors['background.selected.subtlest'] : theme.colors['background.tertiary'],
      },
    });

  return (
    <div
      className={css({
        display: 'inline-flex',
        border: `1px solid ${theme.colors['border.tertiary']}`,
        borderRadius: '5px',
        overflow: 'hidden',
        flexShrink: 0,
        padding: '4px',
        gap: '2px',
      })}
    >
      <button
        type="button"
        onClick={() => onViewModeChange('card')}
        className={buttonStyle(viewMode === 'card')}
        aria-label="Card view"
        aria-pressed={viewMode === 'card'}
      >
        <MdGridOn size={18} />
      </button>
      <button
        type="button"
        onClick={() => onViewModeChange('table')}
        className={buttonStyle(viewMode === 'table')}
        aria-label="Table view"
        aria-pressed={viewMode === 'table'}
      >
        <MdFormatListBulleted size={18} />
      </button>
      <button
        type="button"
        onClick={() => onViewModeChange('graph')}
        className={buttonStyle(viewMode === 'graph')}
        aria-label="Graph view"
        aria-pressed={viewMode === 'graph'}
      >
        <MdOutlineAccountTree size={18} />
      </button>
    </div>
  );
}
